import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, FolderTree, CheckCircle, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BulkCategoryAssignment = ({ connectionId, onComplete }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [byExternalCategory, setByExternalCategory] = useState({});
  const [externalCategories, setExternalCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    loadAwaitingProducts();
    loadCategories();
  }, [connectionId]);

  const loadAwaitingProducts = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/catalogue-pull/products/awaiting-category?limit=200`;
      if (connectionId) url += `&connection_id=${connectionId}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
        setByExternalCategory(data.by_external_category || {});
        setExternalCategories(data.external_categories || []);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/categories/?limit=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCategories(data || []);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory('');
    
    const category = categories.find(c => c.id === categoryId);
    setSubcategories(category?.subcategories || []);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProducts(products.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectByCategory = (extCategory, checked) => {
    const categoryProducts = byExternalCategory[extCategory] || [];
    const categoryIds = categoryProducts.map(p => p.id);
    
    if (checked) {
      setSelectedProducts(prev => [...new Set([...prev, ...categoryIds])]);
    } else {
      setSelectedProducts(prev => prev.filter(id => !categoryIds.includes(id)));
    }
  };

  const handleSelectProduct = (productId, checked) => {
    if (checked) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  const handleBulkAssign = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }
    
    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }

    setAssigning(true);
    try {
      const res = await fetch(`${API_URL}/api/catalogue-pull/products/bulk-assign-category`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_ids: selectedProducts,
          category_id: selectedCategory,
          subcategory_id: selectedSubcategory || null
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setSelectedProducts([]);
        loadAwaitingProducts();
        if (onComplete) onComplete();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to assign category');
      }
    } catch (error) {
      toast.error('Failed to assign category');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">All Caught Up!</h3>
          <p className="text-gray-500 mt-1">No products awaiting category assignment</p>
        </CardContent>
      </Card>
    );
  }

  const selectedCategory_ = categories.find(c => c.id === selectedCategory);

  return (
    <div className="space-y-6" data-testid="bulk-category-assignment">
      {/* Assignment Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderTree className="h-5 w-5" />
            Bulk Category Assignment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Category
              </label>
              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {subcategories.length > 0 && (
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Subcategory
                </label>
                <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                  <SelectTrigger data-testid="subcategory-select">
                    <SelectValue placeholder="Select subcategory (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategories.map(sub => (
                      <SelectItem key={sub.id} value={sub.id}>
                        {sub.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <Button 
              onClick={handleBulkAssign} 
              disabled={assigning || selectedProducts.length === 0 || !selectedCategory}
              data-testid="assign-category-btn"
            >
              {assigning ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Assign to {selectedProducts.length} Products
            </Button>
          </div>
          
          {selectedCategory_ && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm">
              <span className="font-medium">Selected:</span> {selectedCategory_.name}
              {selectedSubcategory && subcategories.find(s => s.id === selectedSubcategory) && (
                <> → {subcategories.find(s => s.id === selectedSubcategory)?.name}</>
              )}
              {selectedCategory_.hsn_code && (
                <Badge variant="outline" className="ml-2">HSN: {selectedCategory_.hsn_code}</Badge>
              )}
              {selectedCategory_.sac_code && (
                <Badge variant="outline" className="ml-2">SAC: {selectedCategory_.sac_code}</Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selection Summary */}
      <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center gap-4">
          <Checkbox
            checked={selectedProducts.length === products.length}
            onCheckedChange={handleSelectAll}
            data-testid="select-all-checkbox"
          />
          <span className="text-sm text-gray-600">
            {selectedProducts.length} of {products.length} products selected
          </span>
        </div>
        {selectedProducts.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setSelectedProducts([])}>
            Clear Selection
          </Button>
        )}
      </div>

      {/* Products Grouped by External Category */}
      <div className="space-y-4">
        {externalCategories.map(extCat => {
          const categoryProducts = byExternalCategory[extCat] || [];
          const categorySelectedCount = categoryProducts.filter(p => 
            selectedProducts.includes(p.id)
          ).length;
          const allSelected = categorySelectedCount === categoryProducts.length;
          
          return (
            <Card key={extCat}>
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={allSelected && categoryProducts.length > 0}
                      onCheckedChange={(checked) => handleSelectByCategory(extCat, checked)}
                    />
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {extCat}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {categoryProducts.length} products • {categorySelectedCount} selected
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline">
                    {categoryProducts.length}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {categoryProducts.map(product => (
                    <div 
                      key={product.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedProducts.includes(product.id) ? 'border-blue-500 bg-blue-50' : ''
                      }`}
                      onClick={() => handleSelectProduct(product.id, !selectedProducts.includes(product.id))}
                    >
                      <Checkbox
                        checked={selectedProducts.includes(product.id)}
                        onCheckedChange={(checked) => handleSelectProduct(product.id, checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {product.images && product.images.length > 0 ? (
                        <img 
                          src={product.images[0]} 
                          alt={product.title}
                          className="w-10 h-10 object-cover rounded"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                          <Package className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {product.title}
                        </p>
                        <p className="text-xs text-gray-500">
                          SKU: {product.sku || 'N/A'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default BulkCategoryAssignment;
